import LoadingTextSkeleton from '@components/skeleton/LoadingTextSkeleton';
import { ActiveCells, DetectionMode, DynamicAnalysis } from '@EcamModel/model/SP111EventMotionDetection';
import { Divider, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import color from '@theme/Colors';
import { ReactNode, useEffect } from 'react';
import { transformScheduleData } from 'src/helpers';
import {
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionHasSubItemProps,
    SectionNotSubItem,
} from '../../../ptz/milesight/components/BasicInfoMilesightCameraPTZ';
import useMileSightSP111, { SectionsCollapseSP111 } from '@pages/cameras/hook/useMilesightSP111';

const dynamicAnalysisEnableMapping: { [key in DynamicAnalysis]: string } = {
    [DynamicAnalysis.Disable]: 'Disable',
    [DynamicAnalysis.Enable]: 'Enable',
};

const activeCellSettingMapping: { [key in ActiveCells]: string } = {
    [ActiveCells.Normal]: 'Normal',
    [ActiveCells.Compatible]: 'Compatible',
};

const detectionModeMapping: { [key in DetectionMode]: string } = {
    [DetectionMode.Normal]: 'Normal',
    [DetectionMode.Advanced]: 'Advanced',
};

type Props = {
    isOpenCollapse: { [key in SectionsCollapseSP111]: boolean };
    handleToggleCollapse: (section: SectionsCollapseSP111) => void;
};

export default function MotionDetectionMilesightsCameraSP111(props: Props) {
    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    const { camera, filteredCamera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const { isLoadingSP111MotionDetection, SP111MotionDetection } = useMileSightSP111({
        filteredCamera,
        cameraSP111: camera.SP111CameraId!,
    });

    const statusList_MotionDetectionHeader: DataSectionHasSubItem[] = [
        {
            label: 'Enable detection:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111MotionDetection}
                    value={`${SP111MotionDetection?.DynamicAnalysisEnable! ? 'Yes' : 'No'}`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Enable motion analysis:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111MotionDetection}
                    value={`${SP111MotionDetection?.MotionEnable! ? 'Yes' : 'No'}`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const statusList_MotionDetection_BasicSettings: DataSectionHasSubItem[] = [
        {
            label: 'Mode:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111MotionDetection}
                    value={detectionModeMapping[SP111MotionDetection?.DetectionMode!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Sensitivity:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111MotionDetection}
                    value={SP111MotionDetection?.TriggerSensitivity!}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Onvif Motion ActiveCells Settings:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111MotionDetection}
                    value={activeCellSettingMapping[SP111MotionDetection?.Activecell!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const dataSectionHasSubItems: ReactNode | DataSectionHasSubItem[] = transformScheduleData(
        SP111MotionDetection?.ScheduleList!,
        (dayName, schedule) => ({
            label: `${dayName}:`,
            value: schedule,
        })
    );

    const statusList_MotionDetection_ScheduleSettings: SectionHasSubItemProps = {
        title: '',
        data: dataSectionHasSubItems,
    };

    return (
        <Stack id="motionDetection_BasicSettings" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            {/* Basic Information */}
            <SectionHasSubItemHeader id="" title="Motion detection" onEdit={handleEdit} />

            <Divider
                orientation="vertical"
                sx={{
                    borderBottomWidth: 1,
                    borderColor: '#E4E7EC',
                    mx: 2,
                }}
            />

            <Stack width={'100%'} padding={'16px 16px'} bgcolor={color.white} gap={2}>
                {statusList_MotionDetectionHeader.map((item, index) => {
                    return (
                        <Grid key={index} xs={12} container>
                            <Grid xs={5}>
                                <Typography>{item.label}</Typography>
                            </Grid>
                            <Grid xs={7}>{item.value}</Grid>
                        </Grid>
                    );
                })}
            </Stack>

            <SectionNotSubItem
                header="Basic Settings"
                statusList={statusList_MotionDetection_BasicSettings}
                isOpen={isOpenCollapse.motionDetection_BasicSettings}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.MotionDetection_BasicSettings);
                }}
            />

            <SectionNotSubItem
                name="motionDetection_ScheduleSettings"
                header="Schedule Settings"
                statusList={statusList_MotionDetection_ScheduleSettings.data}
                isOpen={isOpenCollapse.motionDetection_ScheduleSettings}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.MotionDetection_ScheduleSettings);
                }}
            />
        </Stack>
    );
}
